/**
 * Audio utility functions for speech processing and audio handling
 */

/**
 * Convert base64 string back to audio blob
 */
export const base64ToAudioBlob = (base64: string, mimeType: string = 'audio/mpeg'): Blob => {
  const byteCharacters = atob(base64)
  const byteNumbers = new Array(byteCharacters.length)

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }

  const byteArray = new Uint8Array(byteNumbers)
  return new Blob([byteArray], { type: mimeType })
}

/**
 * The browser's Web Speech recognizer (SpeechRecognition / webkitSpeechRecognition), if any.
 */
export const getSpeechRecognition = (): any =>
  (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition

/**
 * Check if voice input works here: the microphone and the browser's speech recognition, which
 * turns the recording into the text that is sent (Firefox has no recognizer, so no voice input).
 */
export const isAudioRecordingSupported = (): boolean => {
  return typeof navigator.mediaDevices?.getUserMedia === 'function' && !!getSpeechRecognition()
}

// BCP-47 language tags for Web Speech recognition, keyed by SupportedLanguage.
const SPEECH_RECOGNITION_LANG: Record<string, string> = {
  ar: 'ar-SA',
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  pl: 'pl-PL',
  tl: 'fil-PH',
  vi: 'vi-VN',
  zh: 'zh-CN',
}

/**
 * The speech recognition language for an app language ('vi', 'vi-VN', …); en-US when unknown.
 */
export const speechRecognitionLang = (language?: string | null): string =>
  SPEECH_RECOGNITION_LANG[(language ?? '').toLowerCase().split('-')[0]] ?? 'en-US'

/**
 * Check if ElevenLabs TTS is configured and supported
 */
export const isElevenLabsTtsSupported = (): boolean => {
  return !!process.env.REACT_APP_ELEVENLABS_TTS_URL
}

/**
 * Get available audio input devices
 */
export const getAudioInputDevices = async (): Promise<MediaDeviceInfo[]> => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.filter((device) => device.kind === 'audioinput')
  } catch (error) {
    console.error('Error getting audio input devices:', error)
    return []
  }
}

/**
 * Format duration in seconds to MM:SS format
 */
export const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Strip HTML-like tags and normalize whitespace for TTS (avoids truncation from <br>, etc.)
 */
const stripHtmlForTTS = (raw: string): string => {
  return raw
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<(?=[a-zA-Z/!?])[^>]*>?/g, ' ') // strip tags, incl. unclosed e.g. <br; a '<' that starts no tag is text
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Extract text content from message for TTS, stripping markdown and HTML
 */
export const extractTextForTTS = (content: any): string => {
  if (typeof content === 'string') {
    const noMarkdown = content
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1') // Remove italic
      .replace(/`(.*?)`/g, '$1') // Remove code
      .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove links, keep text
      .replace(/#{1,6}\s/g, '') // Remove headers
      // Anchor to start-of-line so we don't eat the `>` from `<br>` etc.
      .replace(/^\s*>\s/gm, '') // Remove blockquotes
      .replace(/\n+/g, ' ') // Replace line breaks with spaces
      .trim()
    return stripHtmlForTTS(noMarkdown)
  }

  if (content && typeof content === 'object') {
    if (content.message) {
      return extractTextForTTS(content.message)
    }

    // `title` is rendered as a tooltip, not spoken; only the body fields
    // contribute. Join them so a form with both `description` and
    // `content` speaks both, instead of returning the first hit.
    const textFields = ['text', 'description', 'content']
    const parts: string[] = []
    for (const field of textFields) {
      if (content[field]) {
        const part = extractTextForTTS(content[field])
        if (part) parts.push(part)
      }
    }
    if (parts.length) return parts.join('. ')
  }

  return ''
}
